import { Compass } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";

import {
  type ProjectCoachingPayload,
  type ProjectFeedEvent,
} from "../../../services/askPunttProjectFeed.types";

/**
 * Per-user coaching card (§10) — a feed item the server only returns to the
 * single user it targets (visibility = userId). Renders the user's numbered
 * action steps plus a CTA to continue privately with Puntt in their personal
 * project chat.
 */
export function FeedCoachingCard({
  event,
  onAskPrivately,
}: {
  event: ProjectFeedEvent;
  onAskPrivately?: () => void;
}) {
  const { t } = useTranslation("ui");
  const payload = (event.payload ?? {}) as ProjectCoachingPayload;
  const steps = payload.actionSteps ?? [];
  const titleId = `feed-coaching-title-${event._id}`;

  return (
    <section
      className="border-puntt-yellow-6 bg-puntt-yellow-2 rounded-xl border p-3.5"
      data-testid="project-feed-coaching-card"
      aria-labelledby={titleId}
    >
      <div className="flex items-center gap-2.5">
        <span
          aria-hidden="true"
          className="bg-puntt-yellow-3 text-puntt-yellow-11 flex size-8 shrink-0 items-center justify-center rounded-full"
        >
          <Compass size={16} weight="fill" />
        </span>
        <h3
          id={titleId}
          className="text-[13px] font-semibold text-puntt-neutral-gray-12"
        >
          {event.body ?? t("feedCoachingCard.defaultTitle")}
        </h3>
      </div>
      {steps.length > 0 && (
        <ol
          className="mt-2 list-decimal space-y-1 pl-7 text-[12.5px] leading-relaxed text-puntt-neutral-gray-12"
          data-testid="project-feed-coaching-steps"
        >
          {steps.map((step, i) => (
            <li key={i} data-testid={`project-feed-coaching-step-${i}`}>
              {step}
            </li>
          ))}
        </ol>
      )}
      {onAskPrivately && (
        <div className="mt-3">
          <button
            type="button"
            data-testid="project-feed-coaching-ask-privately"
            aria-label={t("feedCoachingCard.askPrivately")}
            onClick={onAskPrivately}
            className="border-puntt-yellow-7 hover:bg-puntt-yellow-3 rounded-md border bg-base-white px-2.5 py-1 text-[12px] font-medium text-puntt-neutral-gray-12"
          >
            {t("feedCoachingCard.askPrivately")}
          </button>
        </div>
      )}
    </section>
  );
}
